// Testimonials shown on the subscription paywall.
//
// Quote TEXT lives in the locale files (`payment.testimonials.<segment>`) so a
// German or French member reads the quote in their own language. Attribution —
// the member's name, age and city — stays here as data, because those are real
// people and proper nouns are not translated. This mirrors how the post-quiz
// interstitial already handles its quotes (`quiz.stepper.tableScanQuotes` +
// QUOTE_ATTRIBUTIONS in TableScanInterstitial.tsx).
//
// IMPORTANT — these read as real member quotes. Localizing must never mean
// renaming Marcus from Brooklyn into Lukas from Kreuzberg; that fabricates
// social proof. Translating a real quote is fine. Showing a German member a
// German-language quote genuinely said by a Brooklyn member is fine. Inventing
// a German member is not. To add genuinely local social proof, source real
// quotes from members in that market and register them in LOCAL_ATTRIBUTIONS
// below alongside a matching quote array in that locale's app.json.

export interface Testimonial {
  quote: string;
  name: string;
  age: number;
  city: string;
}

/** Attribution for one quote. Index-aligned with the locale file's quote array. */
export type TestimonialAttribution = Omit<Testimonial, "quote">;

// Paywall testimonials — three coarse segments (men 20-39, men 40+, sapphic broadly).
// Coarser than a per-identity split on purpose: the goal is "people like me are
// getting value" without staging demographic representation.
export type PaywallSegment = "M20_39" | "M40" | "Sapphic";

export const PAYWALL_SEGMENTS: readonly PaywallSegment[] = ["M20_39", "M40", "Sapphic"] as const;

/** Default attribution set — the members whose quotes ship in every locale. */
const DEFAULT_ATTRIBUTIONS: Record<PaywallSegment, TestimonialAttribution[]> = {
  M20_39: [
    { name: "Marcus", age: 27, city: "Brooklyn" },
    { name: "Diego", age: 31, city: "Chicago" },
    { name: "Tyler", age: 24, city: "DC" },
  ],
  M40: [
    { name: "James", age: 47, city: "Los Angeles" },
    { name: "David", age: 52, city: "San Francisco" },
    { name: "Anthony", age: 44, city: "NYC" },
  ],
  Sapphic: [
    { name: "Mei", age: 26, city: "LA" },
    { name: "Jordan", age: 32, city: "Toronto" },
    { name: "Renée", age: 46, city: "Chicago" },
  ],
};

/**
 * Per-language attribution overrides, for when real quotes from members in that
 * market have been sourced and consented. Empty today — de/fr/es members
 * currently read the default members' quotes, translated.
 *
 * To add a market: collect real consented quotes, add the translated strings to
 * that locale's `payment.testimonials.<segment>` array (same length as en), and
 * register the matching attributions here.
 */
const LOCAL_ATTRIBUTIONS: Partial<
  Record<string, Partial<Record<PaywallSegment, TestimonialAttribution[]>>>
> = {};

/** True when this language has its own sourced members for the segment. */
export function hasLocalTestimonials(segment: PaywallSegment, language: string): boolean {
  return Boolean(LOCAL_ATTRIBUTIONS[primaryLanguage(language)]?.[segment]?.length);
}

/** "de-DE" -> "de". i18n normalizes this already; belt and braces for callers. */
function primaryLanguage(language: string): string {
  return (language || "").split("-")[0].toLowerCase();
}

/**
 * Zip translated quote text with attribution.
 *
 * `quotes` comes from i18n and is therefore already in the user's language.
 * Extra quotes without a matching attribution are dropped rather than rendered
 * anonymously — an unattributed testimonial is worth less than no testimonial.
 */
export function buildPaywallTestimonials(
  segment: PaywallSegment,
  language: string,
  quotes: readonly string[]
): Testimonial[] {
  const attributions =
    LOCAL_ATTRIBUTIONS[primaryLanguage(language)]?.[segment] ?? DEFAULT_ATTRIBUTIONS[segment];

  return quotes.flatMap((quote, i) => {
    const attribution = attributions[i];
    if (!attribution || !quote) return [];
    return [{ quote, ...attribution }];
  });
}

// Pick the paywall testimonial bucket from a user's quiz answers. Coarse split:
// men get an age-banded bucket; everyone else (women, nonbinary, etc.) gets sapphic.
// Note: profile fields come in as arrays (genderIdentity / pronouns are multi-select).
export function pickPaywallSegment(input: {
  genderIdentity?: string | string[] | null;
  pronouns?: string | string[] | null;
  birthday?: string | null;
}): PaywallSegment {
  const { genderIdentity, pronouns, birthday } = input;
  const toList = (v: string | string[] | null | undefined): string[] =>
    Array.isArray(v) ? v : v ? [v] : [];
  const genders = toList(genderIdentity);
  const prons = toList(pronouns);
  const isMan =
    genders.includes("cis_man") ||
    genders.includes("trans_man") ||
    prons.includes("he_him") ||
    prons.includes("he_they");
  if (!isMan) return "Sapphic";
  const dob = birthday ? new Date(birthday) : null;
  if (!dob || Number.isNaN(dob.getTime())) return "M20_39";
  const now = new Date();
  let years = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) years -= 1;
  return years >= 40 ? "M40" : "M20_39";
}
